import { Card, Empty } from "antd";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BarList from "./BarList";
import { setLeadFilterState } from "../../Crm/Leads/leadSlice";
import { resolveStatusColor } from "../crmDashboardUtils";

const StatusDistribution = ({ data = [] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { filterState } = useSelector((s) => s.leads);

  // Every status with at least one lead, in full — a pie/donut stops being
  // readable past a handful of slices, and folding the long tail into
  // "Other" was hiding real statuses from view (the exact complaint this
  // replaced: management wants every status visible, not lumped together).
  // A bar list has no such ceiling, so nothing needs to be grouped.
  const { rows, total, idleCount } = useMemo(() => {
    const active = data
      .map((s, i) => ({ ...s, count: Number(s.count) || 0, color: resolveStatusColor(s.color, i) }))
      .filter((s) => s.count > 0)
      .sort((a, b) => b.count - a.count);

    const sum = active.reduce((t, s) => t + s.count, 0);
    const out = active.map((s) => ({
      key: s.id,
      label: s.name,
      value: s.count,
      display: s.count.toLocaleString(),
      secondary: sum ? `${Math.round((s.count / sum) * 100)}%` : "",
      color: s.color,
    }));
    return { rows: out, total: sum, idleCount: data.length - active.length };
  }, [data]);

  const goToStatusLeads = (statusId) => {
    dispatch(setLeadFilterState({ ...filterState, search: "", status: statusId }));
    navigate("/crm/leads");
  };

  return (
    <Card className="crm-card" variant="borderless">
      <div className="crm-card-head">
        <div>
          <div className="crm-card-title">Lead status</div>
          <div className="crm-card-sub">
            <b className="crm-card-figure">{total.toLocaleString()}</b> leads across {rows.length} status{rows.length === 1 ? "" : "es"}
          </div>
        </div>
      </div>

      {total === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No leads yet" style={{ padding: "32px 0" }} />
      ) : (
        // This card sits in a fixed-height row (crm-key-row, 320px) alongside
        // Priority Activities and Open Tasks, which scroll their own
        // overflow internally rather than growing the row — same here, via
        // the shared .crm-rows wrapper those cards already use, so more
        // than ~5 statuses doesn't just get silently clipped.
        <div className="crm-rows">
          <BarList rows={rows} onRowClick={(row) => goToStatusLeads(row.key)} />
        </div>
      )}

      {idleCount > 0 && total > 0 && (
        <div className="crm-card-foot muted">
          {idleCount} more status{idleCount === 1 ? "" : "es"} with no leads
        </div>
      )}
    </Card>
  );
};

export default StatusDistribution;
